import React, { useState, useMemo } from 'react';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { formatCurrency } from '../../data/mockData';
import { Calendar, IndianRupee, FileText, ChevronLeft, ChevronRight, Download, FileSpreadsheet, FolderOpen } from 'lucide-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFolder } from '@fortawesome/free-solid-svg-icons';
import { exportToExcel, exportToPDFWithCapture } from '../../utils/exportUtils';
import DocumentViewerModal from './DocumentViewerModal';

const ProjectTable = ({ projects, selectedDepartment, selectedStatus, title }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [documentModalOpen, setDocumentModalOpen] = useState(false);
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const [selectedProjectName, setSelectedProjectName] = useState(null);

  // Get unique values for filters
  const uniqueDepartments = useMemo(() => {
    const departments = [...new Set(projects.map(p => p.department).filter(Boolean))];
    return departments.sort();
  }, [projects]);

  const uniqueStatuses = useMemo(() => {
    const statuses = [...new Set(projects.map(p => p.status).filter(Boolean))];
    return statuses.sort();
  }, [projects]);

  // Apply filters and remove duplicates
  const filteredProjects = useMemo(() => {
    let filtered = projects.filter(project => {
      const departmentMatch = departmentFilter === 'all' || project.department === departmentFilter;
      const statusMatch = statusFilter === 'all' || project.status === statusFilter;
      return departmentMatch && statusMatch;
    });
    
    // Remove duplicates based on project ID
    const uniqueFiltered = filtered.filter((project, index, arr) => 
      index === arr.findIndex(p => p.id === project.id)
    );
    
    console.log(`Filtered projects: ${filtered.length}, Unique filtered: ${uniqueFiltered.length}`);
    
    return uniqueFiltered;
  }, [projects, departmentFilter, statusFilter]);

  // Calculate unique projects total (for display count)
  const uniqueProjectsTotal = useMemo(() => {
    return projects.filter((project, index, arr) => 
      index === arr.findIndex(p => p.id === project.id)
    ).length;
  }, [projects]);

  // Calculate pagination
  const totalPages = Math.ceil(filteredProjects.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentProjects = filteredProjects.slice(startIndex, endIndex);

  // Smart pagination helper - show limited pages with ellipsis
  const getPaginationPages = () => {
    const maxVisiblePages = 5; // Show max 5 page buttons
    const pages = [];
    
    if (totalPages <= maxVisiblePages) {
      // Show all pages if total is small
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Smart pagination with ellipsis
      const halfVisible = Math.floor(maxVisiblePages / 2);
      let startPage = Math.max(1, currentPage - halfVisible);
      let endPage = Math.min(totalPages, currentPage + halfVisible);
      
      // Adjust if we're near the beginning or end
      if (currentPage <= halfVisible + 1) {
        endPage = Math.min(totalPages, maxVisiblePages);
      } else if (currentPage >= totalPages - halfVisible) {
        startPage = Math.max(1, totalPages - maxVisiblePages + 1);
      }
      
      // Add first page and ellipsis if needed
      if (startPage > 1) {
        pages.push(1);
        if (startPage > 2) {
          pages.push('...');
        }
      }
      
      // Add visible pages
      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }
      
      // Add ellipsis and last page if needed
      if (endPage < totalPages) {
        if (endPage < totalPages - 1) {
          pages.push('...');
        }
        pages.push(totalPages);
      }
    }
    
    return pages;
  };

  // Reset page when projects change or filters are applied
  React.useEffect(() => {
    setCurrentPage(1);
  }, [projects, departmentFilter, statusFilter]);

  // Dynamic title generation
  const getTableTitle = () => {
    if (title) return title;
    if (selectedDepartment) return `${selectedDepartment} Department Projects`;
    if (selectedStatus) return `${selectedStatus} Projects`;
    return 'Projects';
  };

  // Export handlers - Use filtered projects to export only what's displayed
  const handleExportPDF = () => {
    exportToPDFWithCapture(filteredProjects, getTableTitle(), selectedDepartment, selectedStatus);
  };

  const handleExportExcel = () => {
    exportToExcel(filteredProjects, getTableTitle(), selectedDepartment, selectedStatus);
  };

  const getStatusColor = (status) => {
    const colors = {
      'Completed': 'bg-green-100 text-green-800 border-green-200',
      'In Progress': 'bg-blue-100 text-blue-800 border-blue-200',
      'Delayed': 'bg-red-100 text-red-800 border-red-200',
      'Not Started': 'bg-gray-100 text-gray-800 border-gray-200'
    };
    return colors[status] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const getStageColor = (stage) => {
    const colors = {
      'Proposed Works': 'bg-yellow-100 text-yellow-800 border-yellow-200',
      'Work Order Given': 'bg-orange-100 text-orange-800 border-orange-200',
      'Ongoing Works': 'bg-blue-100 text-blue-800 border-blue-200',
      'Completed Works': 'bg-green-100 text-green-800 border-green-200'
    };
    return colors[stage] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    
    // Convert from yyyy-mm-dd format to dd-mm-yyyy
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString; // Return original if invalid
      
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = String(date.getFullYear()); // Get full 4-digit year
      
      return `${day}-${month}-${year}`;
    } catch (error) {
      return dateString; // Return original if parsing fails
    }
  };

  // Format full amount without abbreviations
  const formatFullAmount = (amount) => {
    if (!amount) return '0';
    const numericAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    if (isNaN(numericAmount)) return '0';
    return numericAmount.toLocaleString('en-IN');
  };

  if ((!selectedDepartment && !selectedStatus) || projects.length === 0) {
    return null;
  }

  return (
    <div id="project-listing-section">
      <Card className="p-6 shadow-lg border-0 bg-gradient-to-br from-white to-slate-50">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <div className="p-3 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-xl">
              <FileText className="h-6 w-6 text-white" />
            </div>
            <div>
              <h3 className="text-2xl font-bold text-slate-800">{getTableTitle()}</h3>
              <p className="text-slate-600">Detailed project information and status</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <Badge variant="outline" className="text-teal-600 border-teal-200 bg-teal-50">
              {filteredProjects.length} of {uniqueProjectsTotal} Projects
            </Badge>
            <div className="flex items-center space-x-2">
              <Button
                onClick={handleExportExcel}
                variant="outline"
                size="sm"
                className="border-green-500 text-green-600 hover:bg-green-50 flex items-center space-x-2"
              >
                <FileSpreadsheet className="h-4 w-4" />
                <span>Export to Excel</span>
              </Button>
            </div>
          </div>
        </div>

        {/* Table Filters */}
        <div className="flex flex-wrap items-center gap-4 mb-4 p-4 bg-slate-50 rounded-lg">
          <div className="flex items-center space-x-2">
            <label className="text-sm font-medium text-slate-700">Department:</label>
            <select
              value={departmentFilter}
              onChange={(e) => {
                setDepartmentFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="px-3 py-1 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
            >
              <option value="all">All Departments</option>
              {uniqueDepartments.map(dept => (
                <option key={dept} value={dept}>{dept}</option>
              ))}
            </select>
          </div>
          
          <div className="flex items-center space-x-2">
            <label className="text-sm font-medium text-slate-700">Status:</label>
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="px-3 py-1 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
            >
              <option value="all">All Statuses</option>
              {uniqueStatuses.map(status => (
                <option key={status} value={status}>{status}</option>
              ))}
            </select>
          </div>

          <div className="flex items-center space-x-2">
            <label className="text-sm font-medium text-slate-700">Show:</label>
            <select
              value={itemsPerPage}
              onChange={(e) => {
                setItemsPerPage(parseInt(e.target.value));
                setCurrentPage(1);
              }}
              className="px-3 py-1 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
            >
              <option value={5}>5 per page</option>
              <option value={10}>10 per page</option>
              <option value={20}>20 per page</option>
              <option value={50}>50 per page</option>
            </select>
          </div>

          {(departmentFilter !== 'all' || statusFilter !== 'all') && (
            <Button
              onClick={() => {
                setDepartmentFilter('all');
                setStatusFilter('all');
                setCurrentPage(1);
              }}
              variant="outline"
              size="sm"
              className="text-slate-600 border-slate-300 hover:bg-slate-100"
            >
              Clear Filters
            </Button>
          )}
        </div>

        <div className="overflow-x-auto shadow-inner bg-white rounded-lg">
          <Table className="min-w-full">
            <TableHeader>
              <TableRow className="bg-slate-50 hover:bg-slate-100">
                <TableHead className="font-semibold text-slate-700 w-16">Sr. No.</TableHead>
                <TableHead className="font-semibold text-slate-700" style={{width: '30%'}}>Project Name</TableHead>
                <TableHead className="font-semibold text-slate-700 w-40">Work Type</TableHead>
                <TableHead className="font-semibold text-slate-700 w-48">Fund Head</TableHead>
                <TableHead className="font-semibold text-slate-700 w-32">Department</TableHead>
                <TableHead className="font-semibold text-slate-700 w-32">Agency</TableHead>
                <TableHead className="font-semibold text-slate-700 w-44">TS Details</TableHead>
                <TableHead className="font-semibold text-slate-700 w-48">WO Details</TableHead>
                <TableHead className="font-semibold text-slate-700 w-28">Status</TableHead>
                <TableHead className="font-semibold text-slate-700 w-36">Location</TableHead>
                <TableHead className="font-semibold text-slate-700 w-32 text-center">Uploaded Docs</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {currentProjects.map((project, index) => (
                <TableRow 
                  key={project.id} 
                  className="hover:bg-slate-50/80 transition-colors border-slate-100"
                >
                  <TableCell className="font-medium text-slate-600 w-16">
                    {String(startIndex + index + 1).padStart(2, '0')}
                  </TableCell>
                  <TableCell style={{width: '30%'}}>
                    <div>
                      <p className="font-semibold text-slate-800 break-words leading-tight" title={project.project_name}>
                        {project.project_name}
                      </p>
                      <p className="text-xs text-slate-500 mt-1">{project.fund_category}</p>
                    </div>
                  </TableCell>
                  <TableCell className="w-40">
                    <span className="text-xs text-blue-700 bg-blue-100 px-2 py-1 rounded-md break-words">
                      {project.work_type || 'Not Specified'}
                    </span>
                  </TableCell>
                  <TableCell className="w-48">
                    <div className="text-sm text-slate-600 bg-slate-100 px-2 py-1 rounded-md">
                      <p className="break-words text-xs leading-tight">{project.fund_head}</p>
                    </div>
                  </TableCell>
                  <TableCell className="w-32">
                    <div>
                      <p className="text-sm font-medium text-slate-700">
                        {project.department || 'Not Assigned'}
                      </p>
                      {project.department_marathi && (
                        <p className="text-xs text-slate-500 break-words">{project.department_marathi}</p>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="w-32">
                    <div className="text-sm text-slate-600 bg-orange-100 px-2 py-1 rounded-md">
                      <p className="break-words text-xs leading-tight">
                        {project.agency || 'Not Specified'}
                      </p>
                    </div>
                  </TableCell>
                  <TableCell className="w-44">
                    <div className="space-y-1">
                      <div className="flex items-center space-x-1">
                        <Calendar className="h-3 w-3 text-slate-500" />
                        <span className="text-xs text-slate-600 whitespace-nowrap">
                          {formatDate(project.ts_sanction_date)}
                        </span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <IndianRupee className="h-3 w-3 text-green-600" />
                        <span className="font-semibold text-slate-800 text-xs">
                          ₹{formatFullAmount(project.recap_amt)}
                        </span>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="w-48">
                    <div className="space-y-1">
                      <div className="flex items-center space-x-1">
                        <Calendar className="h-3 w-3 text-slate-500" />
                        <span className="text-xs text-slate-600 whitespace-nowrap">
                          {formatDate(project.work_order_date)}
                        </span>
                      </div>
                      <div className="text-xs text-slate-600 mb-1">
                        <span className="font-medium">WO No:</span> {project.work_order_no || 'N/A'}
                      </div>
                      <div className="flex items-center space-x-1">
                        <IndianRupee className="h-3 w-3 text-blue-600" />
                        <span className="font-semibold text-slate-800 text-xs">
                          ₹{formatFullAmount(project.total_tender_amount)}
                        </span>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="w-28">
                    <Badge className={getStatusColor(project.status)}>
                      {project.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="w-36">
                    <div className="text-xs">
                      {project.longitude && project.latitude && project.longitude !== 0 && project.latitude !== 0 ? (
                        <>
                          <div className="text-slate-600">
                            <span className="font-medium">Lat:</span> {Number(project.latitude).toFixed(6)}
                          </div>
                          <div className="text-slate-600 mt-1">
                            <span className="font-medium">Lng:</span> {Number(project.longitude).toFixed(6)}
                          </div>
                        </>
                      ) : (
                        <span className="text-slate-400">No location data</span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="w-32 text-center">
                    <button
                      onClick={() => {
                        setSelectedProjectId(project.id);
                        setSelectedProjectName(project.project_name);
                        setDocumentModalOpen(true);
                      }}
                      className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-blue-100 hover:bg-blue-200 text-blue-600 hover:text-blue-700 transition-colors group"
                      title="View uploaded documents"
                    >
                      <FontAwesomeIcon 
                        icon={faFolder} 
                        className="text-sm group-hover:scale-110 transition-transform" 
                      />
                    </button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-6 pt-4 border-t border-slate-200">
            <div className="flex items-center space-x-2 text-sm text-slate-600">
              <span>Showing</span>
              <span className="font-semibold">{startIndex + 1}</span>
              <span>to</span>
              <span className="font-semibold">{Math.min(endIndex, filteredProjects.length)}</span>
              <span>of</span>
              <span className="font-semibold">{filteredProjects.length}</span>
              <span>projects</span>
              {filteredProjects.length !== projects.length && (
                <span className="text-slate-500">({projects.length} total)</span>
              )}
            </div>
            
            <div className="flex items-center space-x-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="border-slate-300 text-slate-600 hover:bg-slate-50"
              >
                <ChevronLeft className="h-4 w-4" />
                Previous
              </Button>
              
              <div className="flex items-center space-x-1">
                {getPaginationPages().map((page, index) => {
                  if (page === '...') {
                    return (
                      <span key={`ellipsis-${index}`} className="px-2 py-1 text-slate-400">
                        ...
                      </span>
                    );
                  }
                  
                  return (
                    <Button
                      key={page}
                      variant={currentPage === page ? "default" : "outline"}
                      size="sm"
                      onClick={() => setCurrentPage(page)}
                      className={
                        currentPage === page 
                          ? "bg-teal-600 text-white hover:bg-teal-700" 
                          : "border-slate-300 text-slate-600 hover:bg-slate-50"
                      }
                    >
                      {page}
                    </Button>
                  );
                })}
              </div>
              
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="border-slate-300 text-slate-600 hover:bg-slate-50"
              >
                Next
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </Card>
      
      {/* Document Viewer Modal */}
      <DocumentViewerModal
        isOpen={documentModalOpen}
        onClose={() => setDocumentModalOpen(false)}
        projectId={selectedProjectId}
        projectName={selectedProjectName}
      />
    </div>
  );
};

export default ProjectTable;